import React, { useCallback, useMemo, useState } from 'react';

import { AttentionMessage } from '@eika/attention-message';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { Checkbox } from '@eika/checkbox';
import { IconTypes } from '@eika/icon';

import { Item, ListSkeleton } from '..';
import { PaymentLimitsErrorMessage } from '../../../../components/shared/PaymentLimitsErrorMessage';
import { numberFormat } from '../../../../components/shared/numberFormat';
import { OutboxPaymentItemResult, useOutboxDetailsQuery } from '../../../../fetchHooks/useOutbox';
import usePaymentLimits from '../../../../fetchHooks/usePaymentLimits';
import type { Account, PaymentItemId, UniquePaymentId } from '../../../../generated-types';
import useTranslation, { format, formatJsx } from '../../../../hooks/useTranslation';
import { OutboxWrapper } from '../../../../models/outbox';
import { useSigningContext } from '../../../../signingContext/signingContext';
import getTargetFromAccountFromOutbox from '../../getTargetFromAccountFromOutbox';
import useOutboxState from '../../hooks/useOutboxState';
import EditOutboxModal from '../EditOutboxModal/EditOutboxModal';
import calculateCurrenciesTotals from './calculateCurrenciesTotals';

type OutboxApprovalListContentProps = {
    data: OutboxPaymentItemResult;
    onApproveOutboxItems: (selectedItems: OutboxWrapper[]) => void;
    onApproveSingleOutboxItem: (id: PaymentItemId) => void;
    title?: string;
    supportBulkSigning?: boolean;
    accounts: Account[];
    isRefetching: boolean;
    waitingForSigningFromOthers?: boolean;
};

const ListContent: React.FC<OutboxApprovalListContentProps> = ({
    data,
    onApproveOutboxItems,
    onApproveSingleOutboxItem,
    title,
    supportBulkSigning = true,
    accounts,
    isRefetching,
    waitingForSigningFromOthers = false,
}) => {
    const translated = useTranslation();
    const { signingOperationPending, showSigningFlow } = useSigningContext();
    const { data: paymentLimits } = usePaymentLimits();

    const {
        switchListItem,
        overshootingAmount,
        selectedItems,
        setSelectedItem,
        errorState: [errorMessage, setErrorMessage],
        toggleAllSelected,
    } = useOutboxState(data.items);

    const [openEditModal, setOpenEditModal] = useState(true);
    const [outboxItemToEdit, setOutboxItemToEdit] = useState<OutboxWrapper>();

    const { data: outboxDetails, status: detailsStatus } = useOutboxDetailsQuery(outboxItemToEdit?.paymentItemId);

    const setItemToEditAndModalState = useCallback(
        (modalOpen: boolean, item: OutboxWrapper | undefined) => {
            setOpenEditModal(modalOpen);
            setOutboxItemToEdit(item);
        },
        [setOutboxItemToEdit, setOpenEditModal],
    );

    const signSingleItem = useCallback(
        (itemId: PaymentItemId, outboxId: UniquePaymentId) => {
            setSelectedItem(outboxId.value);
            onApproveSingleOutboxItem(itemId);
        },
        [onApproveSingleOutboxItem, setSelectedItem],
    );

    const selectedItemIds = selectedItems.map((item) => item.uniqueId.value);

    const onApproveMany = useCallback(() => {
        if (selectedItems.length === 0) {
            setErrorMessage(translated.outbox.noSelectedPayments);
            return;
        }

        onApproveOutboxItems(selectedItems);
    }, [onApproveOutboxItems, selectedItems, setErrorMessage, translated.outbox.noSelectedPayments]);

    const currenciesTotals = useMemo(() => calculateCurrenciesTotals(data.itemsToIncludeInTotal), [data]);
    const total = useMemo(
        () =>
            Object.entries(currenciesTotals)
                .map(([currency, amount]) => `${amount} ${currency}`)
                .join(', '),
        [currenciesTotals],
    );

    return (
        <>
            <div className="outbox-approval-list__header">
                <h2>{title}</h2>
                {total && (
                    <span
                        className="outbox-approval-list__total"
                        data-testid={waitingForSigningFromOthers ? 'waiting-for-signing-total' : 'outbox-total'}
                    >
                        {formatJsx(
                            waitingForSigningFromOthers
                                ? translated.outbox.totalForOthersApproval
                                : translated.outbox.totalForYourSigning,
                            <b>{total}</b>,
                        )}
                    </span>
                )}
            </div>
            <ol className="outbox-approval-list__ordered-list">
                {data.items.map((item) => (
                    <li key={item.uniqueId.value}>
                        <Item
                            fromAccount={getTargetFromAccountFromOutbox(accounts, item)}
                            outboxListItem={item}
                            checked={selectedItemIds.includes(item.uniqueId.value)}
                            handleCheckboxSwitch={supportBulkSigning ? switchListItem(item.uniqueId.value) : undefined}
                            signSingleItem={signSingleItem}
                            disabled={signingOperationPending || showSigningFlow}
                            isLoadingDetails={
                                detailsStatus === 'pending' &&
                                item.paymentItemId.value === outboxItemToEdit?.paymentItemId.value
                            }
                            setModalState={(value) => setItemToEditAndModalState(value, value ? item : undefined)}
                        />
                    </li>
                ))}
                {isRefetching && <ListSkeleton count={1} />}

                {outboxItemToEdit && outboxDetails && (
                    <EditOutboxModal
                        onClose={() => setItemToEditAndModalState(false, undefined)}
                        signSingleItem={signSingleItem}
                        outboxItem={outboxItemToEdit}
                        outboxDetails={outboxDetails}
                        modalOpenState={[
                            openEditModal,
                            (value) => setItemToEditAndModalState(value, value ? outboxItemToEdit : undefined),
                        ]}
                    />
                )}
            </ol>

            {supportBulkSigning && (
                <Checkbox
                    label={selectedItems.length > 0 ? translated.outbox.removeChosen : translated.outbox.chooseAll}
                    className="outbox-approval-list__toggle-all"
                    onChange={toggleAllSelected}
                    checked={selectedItems.length > 0}
                />
            )}

            {overshootingAmount > 0 ? (
                <div className="outbox-approval-list-content--error-wrapper">
                    <PaymentLimitsErrorMessage
                        message={format(
                            translated.approveAllPaymentLimitValidation,
                            numberFormat(paymentLimits!.maxMonthlyTransactionAmount, 0),
                            numberFormat(overshootingAmount),
                        )}
                    />
                </div>
            ) : (
                <div className="transaction-form__approve-all-button">
                    {errorMessage && (
                        <AttentionMessage
                            type="error"
                            icon={IconTypes.UTROPSTEGN_24}
                            text={errorMessage}
                            data-testid="no-selections-error-message"
                        />
                    )}
                    {supportBulkSigning && (
                        <ButtonWithSpinner
                            className="transaction-form--submit"
                            buttonType="positive"
                            showSpinner={signingOperationPending || showSigningFlow}
                            onClick={onApproveMany}
                            data-testid="pay-button"
                        >
                            {`${translated.approveAndPay} (${selectedItems.length})`}
                        </ButtonWithSpinner>
                    )}
                </div>
            )}
        </>
    );
};

export default ListContent;
